import React, { createContext, useContext, useEffect, useState } from "react";
import { GET_THEME_DATA } from "../Components/DAM_API/urls";
import Axios, { setAxiosUserId } from "../utils/Axios";

const Dummy = {
  title: "Ecollet",
  metaTitle: "Ecollet – Smart Commerce Platform",
  metaDescription:
    "Ecollet is a modern commerce platform with scalable solutions.",
  metaImage: "https://assets.ecollat.in/KohlerIndia/Logo/Eco_Logo-W400.png",
  logo: "https://assets.ecollat.in/KohlerIndia/Logo/Eco_Logo-W400.png",
  favicon: "https://assets.ecollat.in/KohlerIndia/favicon/512X512.png",
  primaryColor: "#1e293b",
};

const ThemeContext = createContext(null);

/** 🔑 Generate UUID-like user id */
const generateUserId = () =>
  `user_${crypto.randomUUID?.() || Date.now() + "_" + Math.random()}`;

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  useEffect(() => {
    setAxiosUserId(userId);
  }, [userId]);
  useEffect(() => {
    /** ---------------- USER ID ---------------- */
    const existingUserId = localStorage.getItem("userId");

    if (existingUserId) {
      setUserId(existingUserId);
    } else {
      const newUserId = generateUserId();
      localStorage.setItem("userId", newUserId);
      setUserId(newUserId);
    }

    /** ---------------- META / THEME ---------------- */
    const setMetaTag = (name, content, isProperty = false) => {
      if (!content) return;

      const selector = isProperty
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;

      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(isProperty ? "property" : "name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    const setFavicon = (href) => {
      if (!href) return;

      let link =
        document.querySelector("link[rel='icon']") ||
        document.querySelector("link[rel='shortcut icon']");

      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }

      link.type = "image/png";
      link.href = href;
    };

    const loadAnalytics = (ga4Id, gtmId) => {
      if (ga4Id && !window.__GA4_LOADED__) {
        window.__GA4_LOADED__ = true;

        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag() {
          window.dataLayer.push(arguments);
        }
        gtag("js", new Date());
        gtag("config", ga4Id);
      }

      if (gtmId && !window.__GTM_LOADED__) {
        window.__GTM_LOADED__ = true;

        const script = document.createElement("script");
        script.innerHTML = `
          (function(w,d,s,l,i){
            w[l]=w[l]||[];
            w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
            var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
            j.async=true;
            j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
            f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `;
        document.head.appendChild(script);
      }
    };

    const applyThemeSideEffects = (themeData) => {
      window.__Ecollet__Theme = themeData;

      if (themeData?.primaryColor) {
        document.documentElement.style.setProperty(
          "--primary-color",
          themeData.primaryColor,
        );
      }

      document.title = themeData?.metaTitle || themeData?.title || "Ecollet";

      setMetaTag("description", themeData?.metaDescription);
      setMetaTag("og:title", themeData?.metaTitle, true);
      setMetaTag("og:description", themeData?.metaDescription, true);
      setMetaTag("og:image", themeData?.metaImage, true);

      setFavicon(themeData?.favicon);

      loadAnalytics(themeData?.ga4Id, themeData?.gtmId);
    };

    const fetchTheme = async () => {
      try {
        const companySlug = window.location.pathname.split("/")[1];
        const res = await Axios.get(
          GET_THEME_DATA + `?companySlug=${companySlug}`,
        );

        const data = res.data.data;
        const themeData = {
          ...Dummy,
          ...(data.brandName && { title: data.brandName }),
          ...(data.metaTitle && { metaTitle: data.metaTitle }),
          ...(data.metaDescription && {
            metaDescription: data.metaDescription,
          }),
          ...(data.brandLogo && { metaImage: data.brandLogo }),
          ...(data.brandLogo && { logo: data.brandLogo }),
          ...(data.favIcon && { favicon: data.favIcon }),
          ...(data.secondaryColor && { primaryColor: data.secondaryColor }),
          ...(data.g4TrackingCode && { ga4Id: data.g4TrackingCode }),
          ...(data.gtagMetaCode && { gtmId: data.gtagMetaCode }),
          clientId: data.clientId,
        };
        window.Theme = themeData;
        setTheme(themeData);
        applyThemeSideEffects(themeData);
      } catch (err) {
        setTheme(Dummy);
        console.error("Theme load failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, loading, userId }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
